import { TopbarCustomSlot } from '@cw-elements/host-kit';
import type { TopbarNavigationMFEProps } from '@cw-elements/mfe-setup/util';
import { Flex } from 'antd';
import { Suspense, useMemo, useRef } from 'react';

import { ENV_VARIABLES } from '@/constants';
import { useFeatureFlags } from '@/contexts';
import { lazyWithRetry } from '@/utils';

import ErrorBoundary from '../ErrorBoundary';

import { SelectSeason, TopbarFallback } from './components';
import { PANORAMA_APP_NAME, TOPBAR_ERROR_CONTEXT } from './constants';
import { productNameRule } from './styles';
import getPanoramaProductName from './utils/getPanoramaProductName';

const TopbarNavigation = lazyWithRetry(() =>
  import('@cw-elements/mfe-setup/load').then(({ loadRemoteModule }) =>
    loadRemoteModule<TopbarNavigationMFEProps>('topbar-navigation/index')
  )
);

type TopbarProps = {
  hideRegionSeasonSelector?: boolean;
};

const Topbar = ({ hideRegionSeasonSelector = false }: TopbarProps) => {
  const { isEnabled } = useFeatureFlags();
  const automaticSelectionAttempts = useRef(new Set<string>()).current;
  const isSearchTreePopoverEnabled = isEnabled(
    'releaseWebTopbarSearchTreeSelectors'
  );
  const isEnvironmentProductNameEnabled = isEnabled(
    'releaseWebEnvironmentProductName'
  );
  const selectorGap = isSearchTreePopoverEnabled ? 4 : 20;
  const productName = isEnvironmentProductNameEnabled
    ? getPanoramaProductName()
    : null;
  const navigationProps = useMemo(
    () => ({
      onLogout: () => {},
      productImage: {
        redirectUrl: '/',
        imageSrc: '/cropwise-planting-logo.svg',
        name: productName?.name ?? PANORAMA_APP_NAME
      },
      appData: {
        id: ENV_VARIABLES.PANORAMA_APP_ID,
        name: PANORAMA_APP_NAME
      },
      featureConfig: {
        hasSettingsMenu: true,
        hasProfileDropdown: true,
        hasDeckOfApps: true,
        hideEntitiesDropdown: true
      },
      profileDropdownConfig: () => ({
        availableLanguages: ['en'],
        availableUnitSystems: [],
        onLanguageChange: () => {}
      })
    }),
    [productName]
  );

  return (
    <>
      {productName && <style>{productNameRule(productName.variant)}</style>}
      <ErrorBoundary
        errorContext={TOPBAR_ERROR_CONTEXT}
        fallback={
          <TopbarFallback
            automaticSelectionAttempts={automaticSelectionAttempts}
            hideRegionSeasonSelector={hideRegionSeasonSelector}
            selectorGap={selectorGap}
          />
        }>
        <Suspense
          fallback={
            <TopbarFallback
              automaticSelectionAttempts={automaticSelectionAttempts}
              hideRegionSeasonSelector={hideRegionSeasonSelector}
              selectorGap={selectorGap}
            />
          }>
          <TopbarNavigation userLanguage='en' {...navigationProps} />
          {!hideRegionSeasonSelector && (
            <TopbarCustomSlot>
              <Flex gap={selectorGap}>
                <SelectSeason
                  automaticSelectionAttempts={automaticSelectionAttempts}
                />
              </Flex>
            </TopbarCustomSlot>
          )}
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

export default Topbar;
